import React, { useState } from "react";

import { createTheme, ThemeProvider } from "@mui/material/styles";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";

import Condition, { enum_conditional } from "../../models/conditions/Condition";

import ConditionComponent from "./ConditionComponent";
import EditorWrapper from "../ui/EditorWrapper";

import { DatabaseTable } from "../../models/BaseTable";
import { useRowSelector } from "../../store/Hooks";
import { Immutable } from "../../helpers/Util";

const theme = createTheme();

export interface ConditionsToEdit {
  parentConditions: Condition[];
  directConditions: Condition[];
}

interface ConditionsBuilderProps {
  conditions?: ConditionsToEdit;
  setID: number | null;
  title?: string;
  outerTable?: typeof DatabaseTable;
  outerTableID?: number;
  inDrawer?: boolean;
}

const ConditionsEditor = (props: ConditionsBuilderProps) => {
  const [rootID, setRootID] = useState<number | null>(props.setID);
  const root = useRowSelector(Condition, rootID as number);
  const [readonly, setreadonly] = useState<boolean>(false);
  const [viewParents, setViewParents] = useState<boolean>(false);
  const outerTable =
    typeof props.outerTable !== "undefined" &&
    typeof props.outerTableID !== "undefined"
      ? useRowSelector(props.outerTable, props.outerTableID)
      : undefined;

  const createRoot = () => {
    const newRoot = Condition.createInstance({
      conditional: enum_conditional.and,
    });
    setRootID(newRoot.id);
  };

  const updateOuterTable = async (
    outerTable: Immutable<DatabaseTable>,
    condition_id: number | null,
  ) => {
    outerTable = outerTable.update(
      "condition_id" as any,
      condition_id,
    ) as DatabaseTable;
    await outerTable.save();
  };

  const handleSave = async () => {
    if (typeof root !== "undefined") {
      const newRoot = await root.save();
      setRootID(newRoot.id);
      if (typeof outerTable !== "undefined") {
        updateOuterTable(outerTable, newRoot.id);
      }
    }
  };

  const handleDelete = async () => {
    if (typeof outerTable !== "undefined") {
      updateOuterTable(outerTable, null);
    }
    setRootID(null);
  };

  const toggleParentTree = () => {
    if (viewParents) {
      // Reset editor
      setRootID(props.setID);
      setreadonly(false);
      setViewParents(false);
    } else {
      // Make a new root that will contain the parent conditions and my conditions
      const newRoot = Condition.createInstance({
        conditional: enum_conditional.and,
      });
      const parentConditions = props.conditions?.parentConditions ?? [];
      parentConditions.forEach((condition) => {
        if (condition.parent_condition_id === null) {
          newRoot.addChild(condition);
        }
      });
      if (typeof root !== "undefined") {
        newRoot.addChild(root as Condition);
      }
      setreadonly(true);
      setRootID(newRoot.id);
      setViewParents(true);
    }
  };

  let conditions = null;
  if (rootID !== null && typeof root !== "undefined") {
    conditions = (
      <ConditionComponent
        condition_id={rootID}
        index={0}
        readonly={readonly}
      ></ConditionComponent>
    );
  } else {
    conditions = <Button onClick={() => createRoot()}>Add Conditional</Button>;
  }

  return (
    <ThemeProvider theme={theme}>
      <EditorWrapper
        save={handleSave}
        delete={handleDelete}
        table={typeof props.title === "undefined" ? undefined : "Conditions"}
        title={
          typeof props.title === "undefined" ? "Edit Conditions" : props.title
        }
        inDrawer={props.inDrawer}
      >
        <div style={{ padding: "10px" }} data-testid="conditionsEditorTest">
          {typeof props.conditions !== "undefined" &&
            props.conditions?.parentConditions.length > 0 && (
              <Button onClick={toggleParentTree}>
                {viewParents
                  ? "View Only Direct Conditions"
                  : "View Tree With Parent Conditions"}
              </Button>
            )}
          {typeof root !== "undefined" && (
            <FormControl sx={{ width: "100%", marginBottom: "10px" }}>
              <TextField
                name="name"
                label="Condition Set Name"
                value={root.name}
                onChange={(event: React.ChangeEvent<any>) => {
                  if (typeof root !== "undefined") {
                    root.update("name", event.target.value);
                  }
                }}
              ></TextField>
            </FormControl>
          )}
          {conditions}
        </div>
      </EditorWrapper>
    </ThemeProvider>
  );
};

export default ConditionsEditor;
